import {useState, useEffect, useCallback, Fragment} from 'react';
import { Trans } from '@common/i18n/trans';
import { useSettings } from '@common/core/settings/use-settings';
import { Button } from '@common/ui/buttons/button';
import { Dialog } from '@common/ui/overlays/dialog/dialog';
import { DialogHeader } from '@common/ui/overlays/dialog/dialog-header';
import { DialogBody } from '@common/ui/overlays/dialog/dialog-body';
import { DialogFooter } from '@common/ui/overlays/dialog/dialog-footer';
import { useDialogContext } from '@common/ui/overlays/dialog/dialog-context';
import { useForm, useFormContext } from 'react-hook-form';
import {FileWithPath, useDropzone} from 'react-dropzone';
import { Form } from '@common/ui/forms/form';
import { formatBytes } from '@common/utils/number/byte-format';
import { UwFilePayload, useAddSource } from './requests/use-add-source';

export function AddSourceDialog({addFiles}: {addFiles: any}) {
  const form = useForm<UwFilePayload>({
  });
  const {formId, close} = useDialogContext();
  const [files, setFiles] = useState([]);

  const onDrop = useCallback((acceptedFiles: any) => {
   setFiles(acceptedFiles);
	}, []);


  useEffect(() => {
    form.setValue('files', files);
}, [form, files]);
  
  const addSource = useAddSource(form);

  return (
    <Dialog size='800px'>
      <DialogHeader>
        <Trans message="Upload Sources" />
      </DialogHeader>
      <DialogBody>
        <Form
            id={formId}
            form={form}
            onSubmit={value => {
              addSource.mutate(value, {
                onSuccess: (e: any) => {
                  addFiles(e.report_id, e.file_items);
                  close();
                },
              });
              
            }}
          >
          <UwSourceFile onDrop={onDrop}/>
        </Form>
      </DialogBody>
      <DialogFooter>
        <Button
          variant="flat"
          color="orange"
          type="submit"
          form={formId}
          disabled={addSource.isPending}
        >
          <Trans message="Add Source" />
        </Button>
      </DialogFooter>
    </Dialog>
  );
}


interface AcceptsType {
  [key: string]: any[]; // Allows any string key with an array value
}

export function UwSourceFile({onDrop}: any) {

  // const {report: setting_report, uploads} = useSettings();
  
  const [maxFileUpload, setMaxFileUpload] = useState(1);

  const extensions = ['pdf', 'xlsx', 'md', 'txt', 'xml', 'csv', 'json', 'docx'];
  const [accepts, setAccepts] = useState<AcceptsType>({});

  useEffect(() => {
    const newAccepts: AcceptsType = {};
    extensions?.forEach((extension: string) => {
      let mime = '';
      if (extension === 'xml') {
        mime = 'text/xml';
      } else if (extension === 'html') {
        mime = 'text/html';
      } else if (extension === 'csv') {
        mime = 'text/csv';
      } else if (extension === 'md') {
        mime = 'text/markdown';
        newAccepts[mime] = ['.md'];
        newAccepts['text/plain'] = ['.md']; // fallback
        return; // stop here for md
      } else if (extension === 'txt') {
        mime = 'text/plain';
      } else if (extension === 'pdf' || extension === 'json') {
        mime = 'application/' + extension;
      } else if (extension === 'doc') {
        mime = 'application/msword';
      } else if (extension === 'docx') {
        mime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      } else if (extension === 'xlsx') {
        mime = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      } else {
        mime = 'application/' + extension;
      }
      newAccepts[mime] = [`.${extension}`];
    });
    setAccepts(newAccepts);
  }, [extensions]);


  const {
    acceptedFiles,
    getRootProps,
    getInputProps
  } = useDropzone({
    accept: accepts ? accepts : {
      'application/pdf': [],
    },
    onDrop,
    maxFiles: maxFileUpload
  });

  return (
    <section>
      <div {...getRootProps({ className: 'dropzone h-200' })}>
        <input name='files' {...getInputProps()} />
        <p>Drag 'n' drop some files here, or click to select files</p>
        <em>(Only <strong>{extensions?.join(", ")}</strong> will be accepted and <b className='text-orange'>{maxFileUpload} files</b> are the maximum number of files you can drop here)</em>
      </div >
        <div>
        <h4 className='h-40 t-40'><strong>Uploaded files</strong></h4>
        <div className="table-wrp block flex flex-col overflow-y-scroll">
          <table className="w-full file">
            <thead className="bg-white border-b sticky top-0 bg-slate-300">
              <tr className='bg-gray-300'>
              <th scope="col" className="bg-gray-300 text-sm font-medium text-gray-900 px-6 py-4 pl-20 text-left">
                  File Name
                </th>
                <th scope="col" className="bg-gray-300 text-sm font-medium text-gray-900 px-6 py-4 text-left">
                  Size
                </th>
              </tr>
            </thead>
            <tbody className="h-96 overflow-y-auto">
            {acceptedFiles.map((file: FileWithPath, index) => (
              <tr key={file.path} className="bg-white border-b transition duration-300 ease-in-out hover:bg-gray-100">
                <td className="text-sm font-extrabold text-gray-900 font-light px-6 py-4 pl-20 whitespace-nowrap">
                {index+1}. {file.path}
                </td>
                <td className="text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap">{formatBytes(file.size)}</td>
              </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
